import React, { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { getApiUrl } from '../config/api';
import DevAuthHelper from '../utils/DevAuthHelper';

/**
 * ZoomInfo redirects the SSO popup here. The page sends the code to the API
 * with the Colleague Assistant bearer token, then tells the opener to refresh.
 */
const ZoomInfoCallbackPage: React.FC = () => {
  const auth = useAuth();
  const [message, setMessage] = useState('Finishing ZoomInfo sign-in…');

  useEffect(() => {
    if (auth.isLoading) return;
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('error_description') || params.get('error');
    const code = params.get('code');
    const state = params.get('state');
    const token = auth.user?.access_token || DevAuthHelper.getStoredSSOToken() || '';

    if (oauthError) {
      setMessage(oauthError);
      return;
    }
    if (!code || !state) {
      setMessage('ZoomInfo did not return a sign-in code.');
      return;
    }
    if (!token) {
      setMessage('Sign in to Colleague Assistant in this window, then connect ZoomInfo again.');
      return;
    }

    let cancelled = false;
    void fetch(getApiUrl('/zoominfo/callback'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code, state }),
    })
      .then(async (response) => {
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          const detail = typeof body.detail === 'string' ? body.detail : 'ZoomInfo sign-in failed.';
          throw new Error(detail);
        }
        if (cancelled) return;
        window.opener?.postMessage({ type: 'zoominfo-connected' }, window.location.origin);
        setMessage('ZoomInfo is connected. You can close this window.');
        window.close();
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setMessage(error instanceof Error ? error.message : 'ZoomInfo sign-in failed.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [auth.isLoading, auth.user?.access_token]);

  return (
    <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-6">
      <p className="text-sm text-slate-200 text-center max-w-md">{message}</p>
    </div>
  );
};

export default ZoomInfoCallbackPage;
